import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import type { CustomerIndexItemDto, InvoiceDetail } from '@techgitter/contracts';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { changeInvoiceCustomer } from '../../services/invoicesApi';
import { PRIMARY_BTN_SX } from '../../styles/designSystem';

interface ChangeCustomerDialogProps {
  open: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  /**
   * Called with the refreshed invoice after a successful change. `ticketWarning`
   * is set when the invoice moved but the linked ticket couldn't be reassigned
   * (best-effort), so the caller can surface a partial-success message.
   */
  onChanged: (updated: InvoiceDetail, ticketWarning?: string | null) => void;
}

/**
 * Reassign an invoice to a different customer. Picking a customer here posts to
 * `/api/invoices/{number}/customer`, which updates RepairShopr first (source of
 * truth) then the Dataverse mirror. When the invoice is linked to an RS ticket,
 * an opt-in checkbox also reassigns that ticket + its work item.
 *
 * Modal (not a right panel) per the design system: a single-selection action
 * where the underlying page content is irrelevant (skill §6).
 */
export const ChangeCustomerDialog: React.FC<ChangeCustomerDialogProps> = ({
  open,
  invoice,
  onClose,
  onChanged,
}) => {
  const [selected, setSelected] = useState<CustomerIndexItemDto | null>(null);
  const [alsoUpdateTicket, setAlsoUpdateTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected(null);
      setAlsoUpdateTicket(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const hasTicket = invoice.rsTicketId != null;
  const rsCustomerId = selected ? Number(selected.repairShoprCustomerId) : NaN;
  const canSubmit =
    !submitting &&
    selected != null &&
    Number.isFinite(rsCustomerId) &&
    rsCustomerId > 0 &&
    !!selected.dataverseCustomerId;

  const handleSubmit = async () => {
    if (!selected) return;
    if (!Number.isFinite(rsCustomerId) || rsCustomerId <= 0) {
      setError('The selected customer has no RepairShopr link and cannot be used.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const updated = await changeInvoiceCustomer(invoice.number, {
        rsCustomerId,
        dataverseCustomerId: selected.dataverseCustomerId,
        alsoUpdateTicket: hasTicket && alsoUpdateTicket,
      });
      onChanged(updated, updated.ticketWarning ?? null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Change customer</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Reassign invoice <strong>#{invoice.number}</strong> from{' '}
          <strong>{invoice.customerName || 'the current customer'}</strong> to a
          different customer. This updates RepairShopr, then SM4RT.
        </DialogContentText>

        <Box sx={{ mt: 1 }}>
          <CustomerLookupControl
            value={selected}
            onChange={setSelected}
            autoFocus
            disabled={submitting}
          />
        </Box>

        {hasTicket && (
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={alsoUpdateTicket}
                onChange={(e) => setAlsoUpdateTicket(e.target.checked)}
                disabled={submitting}
                size="small"
                sx={{ pt: 0.25 }}
              />
            }
            label={
              <Box sx={{ fontSize: 13, color: '#333' }}>
                Also reassign linked ticket
                {invoice.rsTicketNumber ? ` #${invoice.rsTicketNumber}` : ''} to
                this customer
              </Box>
            }
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : undefined
          }
          sx={PRIMARY_BTN_SX}
        >
          {submitting ? 'Changing…' : 'Change customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
